<template>
  <div
    v-if="isVisible"
    class="fixed inset-0 z-50 flex items-start justify-center top-[100px] px-4 pointer-events-none"
  >
    <div
      class="bg-white rounded-xl shadow-lg border border-gray-200 pointer-events-auto animate-slideDown min-w-[280px] max-w-xs"
      @click.stop
    >
      <!-- 图标和内容 -->
      <div class="flex items-center p-4 space-x-3">
        <!-- 图标 -->
        <div
          class="flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center"
          :class="iconBgClass"
        >
          <component :is="iconComponent" :size="18" :color="iconColor" />
        </div>

        <!-- 消息内容 -->
        <div class="flex-1 min-w-0">
          <p class="text-sm font-medium text-gray-900 truncate">
            {{ title }}
          </p>
          <p v-if="message" class="text-xs text-gray-600 mt-1 leading-relaxed">
            {{ message }}
          </p>
        </div>

        <!-- 关闭按钮 -->
        <button
          v-if="showCloseButton"
          class="flex-shrink-0 w-6 h-6 rounded-full hover:bg-gray-100 flex items-center justify-center transition-colors"
          @click="handleClose"
        >
          <X :size="14" class="text-gray-400" />
        </button>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, watch } from 'vue'
import { CheckCircle, AlertCircle, AlertTriangle, X } from 'lucide-vue-next'

export type ToastType = 'success' | 'error' | 'warning'

interface Props {
  isVisible: boolean
  type?: ToastType
  title: string
  message?: string
  duration?: number // 自动关闭时间，单位毫秒，0表示不自动关闭
  showCloseButton?: boolean
}

interface Emits {
  (e: 'close'): void
  (e: 'update:isVisible', value: boolean): void
}

const props = withDefaults(defineProps<Props>(), {
  type: 'success',
  duration: 3000,
  showCloseButton: true
})

const emit = defineEmits<Emits>()

// 图标组件映射
const iconComponent = computed(() => {
  const icons = {
    success: CheckCircle,
    error: AlertCircle,
    warning: AlertTriangle
  }
  return icons[props.type]
})

// 图标背景样式
const iconBgClass = computed(() => {
  const classes = {
    success: 'bg-green-100',
    error: 'bg-red-100',
    warning: 'bg-yellow-100'
  }
  return classes[props.type]
})

// 图标颜色
const iconColor = computed(() => {
  const colors = {
    success: '#10b981', // green-500
    error: '#ef4444', // red-500
    warning: '#f59e0b' // yellow-500
  }
  return colors[props.type]
})

let autoCloseTimer: NodeJS.Timeout | null = null

const handleClose = () => {
  if (autoCloseTimer) {
    clearTimeout(autoCloseTimer)
    autoCloseTimer = null
  }
  emit('close')
  emit('update:isVisible', false)
}

// 设置自动关闭
const setupAutoClose = () => {
  if (props.duration > 0) {
    autoCloseTimer = setTimeout(() => {
      handleClose()
    }, props.duration)
  }
}

// 监听可见性变化
watch(
  () => props.isVisible,
  newValue => {
    if (newValue) {
      setupAutoClose()
    } else if (autoCloseTimer) {
      clearTimeout(autoCloseTimer)
      autoCloseTimer = null
    }
  }
)

onMounted(() => {
  if (props.isVisible) {
    setupAutoClose()
  }
})
</script>

<style scoped>
@keyframes slideDown {
  from {
    opacity: 0;
    transform: translateY(-12px) scale(0.98);
  }
  to {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
}

.animate-slideDown {
  animation: slideDown 0.3s ease-out;
}
</style>
